import React, { useState, useCallback, type ReactNode } from 'react';
import { clubsService } from '@/features/clubs/services/clubsService';
import type { Business, Event, Artist } from '@/features/clubs/types/types';
import { ClubContext, type ClubContextType } from './ClubContextDefinition';

interface ClubProviderProps {
  children: ReactNode;
}

export const ClubProvider: React.FC<ClubProviderProps> = ({ children }) => {
  const [clubCache, setClubCache] = useState<Map<string, Business>>(new Map());
  const [eventsCache, setEventsCache] = useState<Map<string, Event[]>>(new Map());
  const [artistsCache, setArtistsCache] = useState<Map<string, Artist[]>>(new Map());
  const [loadingStates, setLoadingStates] = useState<Map<string, boolean>>(new Map());
  const [errorStates, setErrorStates] = useState<Map<string, string | null>>(new Map());
  const [currentClubHandle, setCurrentClubHandle] = useState<string | null>(null);

  const getClubData = useCallback((handle: string): Business | null => {
    return clubCache.get(handle) || null;
  }, [clubCache]);

  const getClubEvents = useCallback((handle: string): Event[] | null => {
    return eventsCache.get(handle) || null;
  }, [eventsCache]);

  const getClubArtists = useCallback((handle: string): Artist[] | null => {
    return artistsCache.get(handle) || null;
  }, [artistsCache]);

  const fetchClubData = useCallback(async (handle: string): Promise<Business | null> => {
    // Check if we already have the data
    const cachedData = clubCache.get(handle);
    if (cachedData) {
      return cachedData;
    }

    // Check if we're already loading this club
    if (loadingStates.get(handle)) {
      // Wait for the existing request to complete
      return new Promise((resolve) => {
        const checkCache = () => {
          const data = clubCache.get(handle);
          const loading = loadingStates.get(handle);
          if (data) {
            resolve(data);
          } else if (!loading) {
            resolve(null);
          } else {
            setTimeout(checkCache, 100);
          }
        };
        checkCache();
      });
    }

    try {
      // Set loading state
      setLoadingStates(prev => new Map(prev).set(handle, true));
      setErrorStates(prev => new Map(prev).set(handle, null));

      // Fetch data
      const clubData = await clubsService.getClubByHandle(handle);
      
      // Cache the data
      setClubCache(prev => new Map(prev).set(handle, clubData));
      
      return clubData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch club data';
      setErrorStates(prev => new Map(prev).set(handle, errorMessage));
      return null;
    } finally {
      setLoadingStates(prev => new Map(prev).set(handle, false));
    }
  }, [clubCache, loadingStates]);

  const fetchClubEvents = useCallback(async (handle: string): Promise<Event[] | null> => {
    // Check if we already have the data
    const cachedData = eventsCache.get(handle);
    if (cachedData) {
      return cachedData;
    }

    try {
      const events = await clubsService.getEventsByBusinessHandle(handle);
      setEventsCache(prev => new Map(prev).set(handle, events));
      return events;
    } catch (error) {
      console.error('Error fetching events:', error);
      return null;
    }
  }, [eventsCache]);

  const fetchClubArtists = useCallback(async (handle: string): Promise<Artist[] | null> => {
    // Check if we already have the data
    const cachedData = artistsCache.get(handle);
    if (cachedData) {
      return cachedData;
    }

    try {
      const artists = await clubsService.getArtistsByBusinessHandle(handle);
      setArtistsCache(prev => new Map(prev).set(handle, artists));
      return artists;
    } catch (error) {
      console.error('Error fetching artists:', error);
      return null;
    }
  }, [artistsCache]);

  const isLoading = useCallback((handle: string): boolean => {
    return loadingStates.get(handle) || false;
  }, [loadingStates]);

  const getError = useCallback((handle: string): string | null => {
    return errorStates.get(handle) || null;
  }, [errorStates]);

  const clearCache = useCallback(() => {
    setClubCache(new Map());
    setEventsCache(new Map());
    setArtistsCache(new Map());
    setLoadingStates(new Map());
    setErrorStates(new Map());
  }, []);

  const clearClubCache = useCallback((handle: string) => {
    setClubCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(handle);
      return newCache;
    });
    setEventsCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(handle);
      return newCache;
    });
    setArtistsCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(handle);
      return newCache;
    });
    setLoadingStates(prev => {
      const newStates = new Map(prev);
      newStates.delete(handle);
      return newStates;
    });
    setErrorStates(prev => {
      const newStates = new Map(prev);
      newStates.delete(handle);
      return newStates;
    });
  }, []);

  const setCurrentClub = useCallback((handle: string) => {
    setCurrentClubHandle(handle);
  }, []);

  const invalidateOtherClubs = useCallback((currentHandle: string) => {
    // If we're switching to a different club, clear all other club caches
    if (currentClubHandle && currentClubHandle !== currentHandle) {
      console.log(`Switching from club ${currentClubHandle} to ${currentHandle} - invalidating other clubs`);
      
      setClubCache(prev => {
        const newCache = new Map();
        // Keep only the current club's data
        const currentClubData = prev.get(currentHandle);
        if (currentClubData) {
          newCache.set(currentHandle, currentClubData);
        }
        return newCache;
      });

      setEventsCache(prev => {
        const newCache = new Map();
        const currentEventsData = prev.get(currentHandle);
        if (currentEventsData) {
          newCache.set(currentHandle, currentEventsData);
        }
        return newCache;
      });

      setArtistsCache(prev => {
        const newCache = new Map();
        const currentArtistsData = prev.get(currentHandle);
        if (currentArtistsData) {
          newCache.set(currentHandle, currentArtistsData);
        }
        return newCache;
      });
      
      setLoadingStates(prev => {
        const newStates = new Map();
        // Keep only the current club's loading state
        const currentLoading = prev.get(currentHandle);
        if (currentLoading !== undefined) {
          newStates.set(currentHandle, currentLoading);
        }
        return newStates;
      });
      
      setErrorStates(prev => {
        const newStates = new Map();
        // Keep only the current club's error state
        const currentError = prev.get(currentHandle);
        if (currentError !== undefined) {
          newStates.set(currentHandle, currentError);
        }
        return newStates;
      });
    }
  }, [currentClubHandle]);

  const clearCurrentClub = useCallback(() => {
    setCurrentClubHandle(null);
  }, []);

  // Clear cache when navigating away from club pages entirely
  const clearCacheOnNavigation = useCallback(() => {
    console.log('Navigating away from club pages - clearing all cache');
    setClubCache(new Map());
    setEventsCache(new Map());
    setArtistsCache(new Map());
    setLoadingStates(new Map());
    setErrorStates(new Map());
    setCurrentClubHandle(null);
  }, []);

  const value: ClubContextType = {
    clubCache,
    eventsCache,
    artistsCache,
    loadingStates,
    errorStates,
    currentClubHandle,
    getClubData,
    getClubEvents,
    getClubArtists,
    fetchClubData,
    fetchClubEvents,
    fetchClubArtists,
    isLoading,
    getError,
    clearCache,
    clearClubCache,
    setCurrentClub,
    invalidateOtherClubs,
    clearCurrentClub,
    clearCacheOnNavigation,
  };

  return (
    <ClubContext.Provider value={value}>
      {children}
    </ClubContext.Provider>
  );
};

// Re-export the context and type for convenience
export { ClubContext, type ClubContextType } from './ClubContextDefinition';

